<template>
  <div class="media-delegate">
    <Video
      v-if="contentType === 'video'"
      v-bind="{
        content,
        nextMedia,
        userCompletionStatus,
        parentType,
        parent,
      }"
      ref="currentVideo"
      @heartbeat="handleVideoHeartbeats"
      @update-status="handleVideoStatusUpdate"
      @is-loading="emitLoadingState"
      @is-course-active="updateIsCourseActive"
      @advance-media="$emit('advance-media')"
    />
    <IFrame
      v-if="isOfIFrameType"
      v-bind="{
        content,
        parentType,
        parent,
        userCompletionStatus,
      }"
      @is-course-active="updateIsCourseActive"
      @is-loading="emitLoadingState"
    />
    <Quiz
      v-if="contentType === 'quiz'"
      v-bind="{
        content,
        userCompletionStatus,
        contentTitle,
        nextMedia,
        canStartIssue,
        parent,
        parentType,
      }"
      @is-loading="emitLoadingState"
      @heartbeat="handleQuizHeartbeats"
      @toggle-quiz="$emit('toggleQuiz')"
    />
    <ExercisesList
      v-if="contentType === 'exercise'"
      v-bind="{
        exercises: content.media,
        isUser: hasAccess,
      }"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import CONSTANTS from '@/constants'
import { sendKinesisHeartbeat, sendQuizbeat } from '@/services/heartbeat'
import IFrame from './IFrame.vue'
import Quiz from './Quiz.vue'
import ExercisesList from './ExercisesList.vue'
import Video from './Video.vue'

export default {
  name: 'MediaDelegate',
  components: { IFrame, Quiz, ExercisesList, Video },
  props: {
    parentType: {
      type: String,
      required: true,
    },
    content: {
      type: Object,
      required: true,
    },
    contentTitle: {
      type: String,
      required: false,
    },
    userCompletionStatus: {
      type: String,
      required: false,
    },
    nextMedia: {
      type: [String, Object],
      required: false,
    },
    canStartIssue: {
      type: Boolean,
      required: false,
    },
    hasAccess: {
      type: Boolean,
      required: false,
    },
    manualParent: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      isUpdatingUserCompletionStatus: false,
      isUpdatingIsActiveCourse: false,
    }
  },
  computed: {
    ...mapState({
      currentCourse: state => state.courses.currentCourse,
      currentLearningPath: state => state.learningPaths.currentLearningPath,
      currentBootcamp: state => state.bootcamps.selectedBootcamp,
      activeCourses: state => state.courses.activeCourses,
      profile: state => state.auth.userProfile,
      isImpersonating: state => state.admin.isImpersonating,
      currentQuiz: state => state.quizzes.currentQuiz,
    }),
    isCourseActive() {
      if (this.parentType === 'course') {
        return !!this.activeCourses.find(
          activeCourse => activeCourse.id === this.parent.id
        )
      }
      return false
    },
    parent() {
      if (this.manualParent) {
        return {
          ...this.manualParent,
          id: this.manualParent.objectID
            ? this.manualParent.objectID
            : this.manualParent.id,
        }
      }
      if (this.parentType === 'course') {
        return this.currentCourse
      }
      if (this.parentType === 'learning path') {
        return this.currentLearningPath
      }
      if (this.parentType === 'bootcamp') {
        return this.currentBootcamp
      }
      return this.currentCourse
    },
    contentType() {
      const {
        content: { type },
      } = this
      return type
    },
    isOfIFrameType() {
      const iFrameTypes = ['slides', 'iframe']
      return iFrameTypes.includes(this.contentType)
    },
  },
  methods: {
    emitLoadingState(state) {
      this.$emit('is-loading', state)
    },
    handleVideoHeartbeats({ delta, position }) {
      if (this.parentType === 'course' && !this.isImpersonating) {
        const {
          profile: { uaaId: user_id, account_id, team_ids },
          content: { id: video_id },
        } = this
        const { currentCourse: course } = this
        sendKinesisHeartbeat({
          position,
          delta,
          user_id,
          account_id,
          team_ids,
          video_id,
          course_id: course && course.id ? course.id : '',
          course_slug: course && course.slug ? course.slug : '',
          instructor_id:
            course && course.instructors && course.instructors[0]
              ? course.instructors[0].id
              : '',
        })
      }
    },
    async handleVideoStatusUpdate() {
      const hasWatched =
        this.userCompletionStatus === CONSTANTS.CONTENT_USER_STATUS.FINISHED

      if (!this.isUpdatingUserCompletionStatus && !hasWatched) {
        this.isUpdatingUserCompletionStatus = true
        await this.$store.dispatch('content/updateVideoStatus', {
          videoId: this.content.id,
          status: CONSTANTS.CONTENT_USER_STATUS.FINISHED,
        })
        this.$store.dispatch('content/updateLegacyStatus', {
          userId: this.profile.content_api_id,
          videoId: this.content.id,
        })
        await this.$store.dispatch('content/getVideoStatus', this.content.id)
        if (this.parentType === 'course') {
          setTimeout(async () => {
            await this.$store.dispatch(
              'courses/getCourseById',
              this.currentCourse.id
            )
          }, 500)

          this.sendMixpanelEvent(this.eventsConstants.CONTENT_VIDEO_FINISHED, {
            course_id: this.currentCourse.id,
            course_name: this.currentCourse.name,
            video_id: this.content.id,
            video_name: this.content.name,
            ...this.content,
          })
        }
        if (this.parentType === 'bootcamp') {
          this.sendMixpanelEvent(this.eventsConstants.CONTENT_VIDEO_FINISHED, {
            bootcamp_id: this.parent.id,
            bootcamp_name: this.parent.name,
            video_id: this.content.id,
            video_name: this.content.name,
            ...this.content,
          })
        }
        this.isUpdatingUserCompletionStatus = false
      }
    },
    handleQuizHeartbeats({ score, time_elapsed }) {
      if (this.parentType === 'course' && !this.isImpersonating) {
        const {
          profile: { uaaId: user_id, account_id, team_ids },
          content: { id: quiz_id },
          currentCourse: { id: course_id, slug: course_slug, instructors },
          currentQuiz: {
            metadata: { video_reference: video_id },
          },
        } = this
        const instructor_id = instructors[0].id
        sendQuizbeat({
          user_id,
          account_id,
          team_ids,
          quiz_id,
          score,
          time_elapsed,
          video_id,
          course_id,
          course_slug,
          instructor_id,
        })
      }
    },
    async updateIsCourseActive() {
      if (this.parentType === 'course') {
        if (!this.isCourseActive && !this.isUpdatingIsActiveCourse) {
          this.isUpdatingIsActiveCourse = true
          await this.$store.dispatch('courses/addActiveCourse', this.parent.id)
          await this.$store.dispatch('courses/getActiveCourses')
          this.sendMixpanelEvent(
            this.eventsConstants.CONTENT_COURSE_ACTIVE_ADDED,
            {
              course_id: this.parent.id,
              course_name: this.parent.name,
              trigger: 'video',
              ...this.parent,
            }
          )
          this.isUpdatingIsActiveCourse = false
        }
      }
    },
  },
}
</script>

<style></style>
